import { RelativeComponentLoader } from "@/components/loader";
import { getDbtModelsFilters, getUserGroupDetails } from "@/helpers/apis";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, AlertDescription, Card, Stack, Typography } from "@altimateai/lego";
import { isAdmin, isOwner } from "@/services/auth";
import useUserGroups from "./useUserGroups";
import dayjs from "dayjs";
import { useUsers } from "@/helpers/hooks";
import DeleteWithConfirm from "./DeleteWithConfirm";
import { useAppState } from "@/modules/app/useAppContext";
import { isUserDeleted } from "@/helpers/utils";
import AccessRulesSection from "./AccessRulesSection";
import OwnershipSection from "./OwnershipSection";
import MembersSection from "./MembersSection";
import { DbtModelFiltersResponse } from "@/types/dbtModels";

const UserGroupDetails = () => {
  const { datastores, costAttributionEnabled, accessRulesDisabled } = useAppState();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { groups } = useUserGroups();
  const { getById } = useUsers(true, true); // Include deleted users
  const {
    data: groupDetails,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["UserGroupDetails", id],
    queryFn: () => {
      if (id) return getUserGroupDetails(id);
      return null;
    },
    enabled: Boolean(id),
  });

  const { data: dbtIntegrationsResponse } = useQuery<DbtModelFiltersResponse | undefined>({
    queryKey: ["dbtIntegrationsResponse"],
    queryFn: getDbtModelsFilters,
  });

  const dbtIntegrations =
    dbtIntegrationsResponse?.dbt_integrations ??
    ([] as DbtModelFiltersResponse["dbt_integrations"]);

  const getUserName = (userId: string) => {
    const user = getById(parseInt(userId));
    if (!user) return "";

    return isUserDeleted(user)
      ? `${user.first_name} ${user.last_name} (deleted)`
      : `${user.first_name} ${user.last_name}`;
  };

  const canDeleteEntity = (type: "rule" | "user" | "ownership") => {
    if (groupDetails?.is_deleted) {
      return false;
    }

    if (groupDetails?.is_owner) {
      return type === "user";
    }

    return isOwner();
  };

  const canDeleteTeam = () => {
    if (groupDetails?.is_deleted) {
      return false;
    }

    return isOwner() || isAdmin();
  };

  const handleTeamDeleteSuccess = () => {
    // Refresh the user groups list to remove the deleted team
    queryClient.invalidateQueries({ queryKey: ["UserGroups"] });
    // Navigate back to the teams list after successful deletion
    navigate("/usergroups");
  };

  if (!groups.length) {
    return null;
  }

  if (!id) {
    return (
      <Card className="al-flex-1 al-p-3 al-justify-between al-items-center">
        <h4>Please select a team from left side</h4>
      </Card>
    );
  }

  if (isFetching) {
    return (
      <div className="al-flex-1">
        <RelativeComponentLoader label="Loading team details..." />
      </div>
    );
  }

  if (!groupDetails) {
    return (
      <Alert className="al-flex-1" variant="destructive">
        <AlertDescription>User group not found!</AlertDescription>
      </Alert>
    );
  }

  const canManageRules = canDeleteEntity("rule");
  const canManageOwnership = canDeleteEntity("ownership");
  const canManageUsers = canDeleteEntity("user");

  return (
    <Card className="al-flex-1 al-p-3">
      <Stack direction="row" justify="between" align="start" className="al-mb-4">
        <div>
          <Stack direction="row" align="center" gap="sm">
            <Typography variant="h4">{groupDetails.name}</Typography>
            {groupDetails.is_deleted && (
              <span className="al-px-2 al-py-1 al-bg-red-100 al-text-red-800 al-rounded al-text-sm al-font-medium">
                Deactivated
              </span>
            )}
          </Stack>
          <Typography variant="body">{groupDetails.description}</Typography>
        </div>
        {canDeleteTeam() && (
          <Stack direction="row" align="center" gap="sm">
            <DeleteWithConfirm
              entityId={id}
              id=""
              refetch={handleTeamDeleteSuccess}
              entityType="usergroup"
              title={`Delete team ${groupDetails.name}`}
            />
          </Stack>
        )}
      </Stack>

      <Alert className="al-border-0 al-bg-bg-brand al-text-gray">
        <span className="al-me-3 ">
          Created by:{" "}
          <b>
            {getUserName(groupDetails.created_by)} on{" "}
            {dayjs(groupDetails.created_at).format("DD MMM YYYY hh:mm")}
          </b>
        </span>
        <span>
          Updated by:{" "}
          <b>
            {getUserName(groupDetails.updated_by)} on{" "}
            {dayjs(groupDetails.updated_at).format("DD MMM YYYY hh:mm")}
          </b>
        </span>
      </Alert>

      <AccessRulesSection
        rules={groupDetails.user_group_rules}
        datastores={datastores}
        dbtIntegrations={dbtIntegrations}
        getUserName={getUserName}
        canManageRules={canManageRules}
        isGroupDeleted={!!groupDetails.is_deleted}
        groupId={id}
        refetch={refetch}
        accessRulesDisabled={Boolean(accessRulesDisabled)}
      />

      {costAttributionEnabled && (
        <OwnershipSection
          ownerships={groupDetails.user_group_ownership}
          getUserName={getUserName}
          canManageOwnership={canManageOwnership}
          isGroupDeleted={!!groupDetails.is_deleted}
          groupId={id}
          refetch={refetch}
        />
      )}

      <MembersSection
        members={groupDetails.users}
        canManageUsers={canManageUsers}
        groupId={id}
        refetch={refetch}
        isGroupDeleted={!!groupDetails.is_deleted}
      />
    </Card>
  );
};

export default UserGroupDetails;
